import React, { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  Globe,
  Users,
  Database,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Activity,
  Calendar,
  Download,
  Search,
  BarChart3,
  PieChart,
  FileText,
  Package,
  Upload,
  Eye,
  RefreshCw,
} from 'lucide-react';
import { useAdminPermissions } from '@/hooks/useAdminPermissions';
import { formatDistanceToNow } from 'date-fns';
import { logDebug } from '@/utils/errorHandler';

interface Organization {
  id: string;
  name: string;
  plan: 'free' | 'basic' | 'professional' | 'enterprise';
  status: 'active' | 'inactive' | 'suspended';
  userCount: number;
  productCount: number;
  storageUsed: number;
  storageLimit: number;
  apiRequestsLast30Days: number;
  monthlyRevenue: number;
  createdAt: string;
  lastActive: string;
  billingEmail: string;
  industry: string;
  country: string;
}

interface UsageMetrics {
  totalUsers: number;
  activeUsers: number;
  totalProducts: number;
  totalStorage: number;
  apiRequests: number;
  monthlyRevenue: number;
  conversionRate: number;
  churnRate: number;
}

interface TimeSeriesData {
  date: string;
  users: number;
  products: number;
  apiRequests: number;
  revenue: number;
}

export function OrganizationAnalytics() {
  const { hasPermission } = useAdminPermissions();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [filteredOrgs, setFilteredOrgs] = useState<Organization[]>([]);
  const [usageMetrics, setUsageMetrics] = useState<UsageMetrics | null>(null);
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesData[]>([]);
  const [filter, setFilter] = useState({
    search: '',
    plan: 'all',
    status: 'all',
    industry: 'all',
    period: '30d',
  });
  const [loading, setLoading] = useState(true);

  // Mock data - replace with real API calls
  useEffect(() => {
    const mockOrganizations: Organization[] = [
      {
        id: '1',
        name: 'Acme Corporation',
        plan: 'enterprise',
        status: 'active',
        userCount: 25,
        productCount: 1250,
        storageUsed: 4.2,
        storageLimit: 10,
        apiRequestsLast30Days: 125000,
        monthlyRevenue: 499,
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 180).toISOString(),
        lastActive: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
        billingEmail: 'billing@acmecorp.com',
        industry: 'Technology',
        country: 'United States',
      },
      {
        id: '2',
        name: 'Global Retail Inc',
        plan: 'professional',
        status: 'active',
        userCount: 12,
        productCount: 850,
        storageUsed: 2.8,
        storageLimit: 5,
        apiRequestsLast30Days: 85000,
        monthlyRevenue: 199,
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 90).toISOString(),
        lastActive: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
        billingEmail: 'billing@globalretail.com',
        industry: 'Retail',
        country: 'Canada',
      },
      {
        id: '3',
        name: 'StartupCo',
        plan: 'basic',
        status: 'active',
        userCount: 3,
        productCount: 125,
        storageUsed: 0.5,
        storageLimit: 1,
        apiRequestsLast30Days: 15000,
        monthlyRevenue: 49,
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 45).toISOString(),
        lastActive: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
        billingEmail: 'billing@startupco.com',
        industry: 'Technology',
        country: 'United Kingdom',
      },
      {
        id: '4',
        name: 'Manufacturing Ltd',
        plan: 'free',
        status: 'inactive',
        userCount: 1,
        productCount: 25,
        storageUsed: 0.1,
        storageLimit: 0.5,
        apiRequestsLast30Days: 500,
        monthlyRevenue: 0,
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 120).toISOString(),
        lastActive: new Date(Date.now() - 1000 * 60 * 60 * 24 * 30).toISOString(),
        billingEmail: 'info@manufacturing.com',
        industry: 'Manufacturing',
        country: 'Germany',
      },
    ];

    const mockMetrics: UsageMetrics = {
      totalUsers: 41,
      activeUsers: 37,
      totalProducts: 2250,
      totalStorage: 7.6,
      apiRequests: 225500,
      monthlyRevenue: 747,
      conversionRate: 12.5,
      churnRate: 3.2,
    };

    const mockTimeSeriesData: TimeSeriesData[] = Array.from({ length: 30 }, (_, i) => ({
      date: new Date(Date.now() - 1000 * 60 * 60 * 24 * (29 - i)).toISOString().split('T')[0],
      users: Math.floor(35 + Math.random() * 10),
      products: Math.floor(2000 + Math.random() * 500),
      apiRequests: Math.floor(200000 + Math.random() * 50000),
      revenue: Math.floor(700 + Math.random() * 100),
    }));

    setTimeout(() => {
      setOrganizations(mockOrganizations);
      setFilteredOrgs(mockOrganizations);
      setUsageMetrics(mockMetrics);
      setTimeSeriesData(mockTimeSeriesData);
      setLoading(false);
    }, 1000);
  }, []);

  // Filter organizations
  useEffect(() => {
    let filtered = organizations;

    if (filter.search) {
      filtered = filtered.filter(
        org =>
          org.name.toLowerCase().includes(filter.search.toLowerCase()) ||
          org.industry.toLowerCase().includes(filter.search.toLowerCase()) ||
          org.country.toLowerCase().includes(filter.search.toLowerCase())
      );
    }

    if (filter.plan !== 'all') {
      filtered = filtered.filter(org => org.plan === filter.plan);
    }

    if (filter.status !== 'all') {
      filtered = filtered.filter(org => org.status === filter.status);
    }

    if (filter.industry !== 'all') {
      filtered = filtered.filter(org => org.industry === filter.industry);
    }

    setFilteredOrgs(filtered);
  }, [organizations, filter]);

  const getPlanBadge = (plan: string) => {
    switch (plan) {
      case 'free':
        return <Badge className="bg-gray-100 text-gray-800">Free</Badge>;
      case 'basic':
        return <Badge className="bg-blue-100 text-blue-800">Basic</Badge>;
      case 'professional':
        return <Badge className="bg-purple-100 text-purple-800">Professional</Badge>;
      case 'enterprise':
        return <Badge className="bg-gold-100 text-gold-800">Enterprise</Badge>;
      default:
        return <Badge variant="secondary">{plan}</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800">Active</Badge>;
      case 'inactive':
        return <Badge className="bg-gray-100 text-gray-800">Inactive</Badge>;
      case 'suspended':
        return <Badge className="bg-red-100 text-red-800">Suspended</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getStorageUsagePercent = (used: number, limit: number) => {
    return Math.round((used / limit) * 100);
  };

  const industries = [...new Set(organizations.map(org => org.industry))];

  const exportData = () => {
    // Simulate data export
    logDebug('Exporting organization analytics data', {
      organizationCount: filteredOrgs.length,
      filter,
      timestamp: new Date().toISOString(),
    });
  };

  if (!hasPermission('admin.analytics.view')) {
    return (
      <AdminLayout title="Access Denied">
        <div className="py-8 text-center">
          <p>You don't have permission to view organization analytics.</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Organization Analytics"
      description="Monitor organization usage, performance, and revenue metrics"
    >
      <div className="space-y-6">
        {/* Header Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search organizations..."
                value={filter.search}
                onChange={e => setFilter({ ...filter, search: e.target.value })}
                className="w-64 pl-10"
              />
            </div>

            <Select
              value={filter.plan}
              onValueChange={value => setFilter({ ...filter, plan: value })}
            >
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Plan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plans</SelectItem>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="basic">Basic</SelectItem>
                <SelectItem value="professional">Professional</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filter.status}
              onValueChange={value => setFilter({ ...filter, status: value })}
            >
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filter.period}
              onValueChange={value => setFilter({ ...filter, period: value })}
            >
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="90d">Last 90 days</SelectItem>
                <SelectItem value="1y">Last year</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={exportData}>
              <Download className="mr-2 size-4" />
              Export Data
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        {usageMetrics && (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Organizations</CardTitle>
                <Globe className="size-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{organizations.length}</div>
                <p className="text-xs text-muted-foreground">
                  {organizations.filter(o => o.status === 'active').length} active
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Users</CardTitle>
                <Users className="size-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{usageMetrics.totalUsers}</div>
                <p className="text-xs text-muted-foreground">
                  {usageMetrics.activeUsers} active users
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle>
                <DollarSign className="size-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">${usageMetrics.monthlyRevenue}</div>
                <p className="flex items-center text-xs text-muted-foreground">
                  <TrendingUp className="mr-1 size-3" />
                  +12.5% from last month
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Products</CardTitle>
                <Package className="size-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {usageMetrics.totalProducts.toLocaleString()}
                </div>
                <p className="text-xs text-muted-foreground">Across all organizations</p>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="organizations">Organizations</TabsTrigger>
            <TabsTrigger value="usage">Usage Analytics</TabsTrigger>
            <TabsTrigger value="revenue">Revenue</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* Performance Metrics */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <BarChart3 className="size-5" />
                    <span>Growth Metrics</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="mb-2 flex justify-between text-sm">
                      <span>User Growth Rate</span>
                      <span className="font-medium text-green-600">+8.3%</span>
                    </div>
                    <Progress value={83} className="h-2" />
                  </div>
                  <div>
                    <div className="mb-2 flex justify-between text-sm">
                      <span>Revenue Growth</span>
                      <span className="font-medium text-green-600">+12.5%</span>
                    </div>
                    <Progress value={89} className="h-2" />
                  </div>
                  <div>
                    <div className="mb-2 flex justify-between text-sm">
                      <span>Product Growth</span>
                      <span className="font-medium text-blue-600">+15.7%</span>
                    </div>
                    <Progress value={92} className="h-2" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Activity className="size-5" />
                    <span>Usage Statistics</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">API Requests (30d)</p>
                      <p className="text-xs text-muted-foreground">
                        {usageMetrics?.apiRequests.toLocaleString()} requests
                      </p>
                    </div>
                    <TrendingUp className="size-8 text-green-600" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Storage Used</p>
                      <p className="text-xs text-muted-foreground">
                        {usageMetrics?.totalStorage} GB total
                      </p>
                    </div>
                    <Database className="size-8 text-blue-600" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Conversion Rate</p>
                      <p className="text-xs text-muted-foreground">
                        {usageMetrics?.conversionRate}% trial to paid
                      </p>
                    </div>
                    <TrendingUp className="size-8 text-purple-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Top Performing Organizations */}
            <Card>
              <CardHeader>
                <CardTitle>Top Performing Organizations</CardTitle>
                <CardDescription>Organizations with highest usage and revenue</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {organizations
                    .sort((a, b) => b.monthlyRevenue - a.monthlyRevenue)
                    .slice(0, 5)
                    .map((org, index) => (
                      <div
                        key={org.id}
                        className="flex items-center justify-between rounded-lg bg-gray-50 p-3"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="flex size-8 items-center justify-center rounded-full bg-blue-100 text-sm font-medium">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-medium">{org.name}</p>
                            <p className="text-sm text-muted-foreground">{org.industry}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">${org.monthlyRevenue}/mo</p>
                          <p className="text-sm text-muted-foreground">{org.userCount} users</p>
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="organizations" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Organizations ({filteredOrgs.length})</CardTitle>
                <CardDescription>
                  Detailed view of all organizations and their metrics
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Organization</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Users</TableHead>
                      <TableHead>Products</TableHead>
                      <TableHead>Storage</TableHead>
                      <TableHead>Revenue</TableHead>
                      <TableHead>Last Active</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={8} className="py-8 text-center">
                          Loading organizations...
                        </TableCell>
                      </TableRow>
                    ) : filteredOrgs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="py-8 text-center">
                          No organizations found matching your criteria
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredOrgs.map(org => (
                        <TableRow key={org.id}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{org.name}</div>
                              <div className="text-sm text-muted-foreground">{org.industry}</div>
                            </div>
                          </TableCell>
                          <TableCell>{getPlanBadge(org.plan)}</TableCell>
                          <TableCell>{getStatusBadge(org.status)}</TableCell>
                          <TableCell>{org.userCount}</TableCell>
                          <TableCell>{org.productCount.toLocaleString()}</TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="text-sm">
                                {org.storageUsed} GB / {org.storageLimit} GB
                              </div>
                              <Progress
                                value={getStorageUsagePercent(org.storageUsed, org.storageLimit)}
                                className="h-1"
                              />
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="font-medium">${org.monthlyRevenue}/mo</span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">
                              {formatDistanceToNow(new Date(org.lastActive), {
                                addSuffix: true,
                              })}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="usage" className="space-y-6">
            {/* Usage Charts would go here */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>User Growth Trend</CardTitle>
                  <CardDescription>Daily active users over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex h-64 items-center justify-center text-muted-foreground">
                    <div className="text-center">
                      <BarChart3 className="mx-auto mb-2 size-12" />
                      <p>User growth chart would be rendered here</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>API Usage</CardTitle>
                  <CardDescription>API requests per day</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex h-64 items-center justify-center text-muted-foreground">
                    <div className="text-center">
                      <Activity className="mx-auto mb-2 size-12" />
                      <p>API usage chart would be rendered here</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="revenue" className="space-y-6">
            {/* Revenue Analytics */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">MRR</CardTitle>
                  <DollarSign className="size-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">$747</div>
                  <p className="flex items-center text-xs text-muted-foreground">
                    <TrendingUp className="mr-1 size-3" />
                    +12.5% from last month
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">ARPU</CardTitle>
                  <Users className="size-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">$187</div>
                  <p className="text-xs text-muted-foreground">Average revenue per user</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Churn Rate</CardTitle>
                  <TrendingDown className="size-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">3.2%</div>
                  <p className="text-xs text-muted-foreground">Monthly churn rate</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Revenue by Plan</CardTitle>
                <CardDescription>Monthly recurring revenue breakdown</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="bg-gold-500 size-4 rounded"></div>
                      <span>Enterprise</span>
                    </div>
                    <div className="text-right">
                      <span className="font-medium">$499</span>
                      <span className="ml-2 text-sm text-muted-foreground">66.8%</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="size-4 rounded bg-purple-500"></div>
                      <span>Professional</span>
                    </div>
                    <div className="text-right">
                      <span className="font-medium">$199</span>
                      <span className="ml-2 text-sm text-muted-foreground">26.6%</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="size-4 rounded bg-blue-500"></div>
                      <span>Basic</span>
                    </div>
                    <div className="text-right">
                      <span className="font-medium">$49</span>
                      <span className="ml-2 text-sm text-muted-foreground">6.6%</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
